import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Cpu,
  Zap,
  DollarSign,
  AlertCircle,
  Info,
} from 'lucide-react'
import { type AgentBuilderState } from '../AgentBuilder'

interface ModelStepProps {
  state: AgentBuilderState
  updateState: (updates: Partial<AgentBuilderState>) => void
  errors: Record<string, string>
}

interface ModelOption {
  id: string
  name: string
  provider: string
  description: string
  contextWindow: string
  costPer1kTokens: number
  speed: 'fast' | 'medium' | 'slow'
  capabilities: string[]
}

const MODELS: ModelOption[] = [
  {
    id: 'gpt-4o',
    name: 'GPT-4o',
    provider: 'OpenAI',
    description: 'Most capable model with multimodal abilities',
    contextWindow: '128K',
    costPer1kTokens: 0.005,
    speed: 'medium',
    capabilities: ['Text', 'Vision', 'Code', 'Reasoning'],
  },
  {
    id: 'gpt-4o-mini',
    name: 'GPT-4o Mini',
    provider: 'OpenAI',
    description: 'Fast and cost-effective for most tasks',
    contextWindow: '128K',
    costPer1kTokens: 0.00015,
    speed: 'fast',
    capabilities: ['Text', 'Vision', 'Code'],
  },
  {
    id: 'claude-3-5-sonnet',
    name: 'Claude 3.5 Sonnet',
    provider: 'Anthropic',
    description: 'Excellent balance of capability and speed',
    contextWindow: '200K',
    costPer1kTokens: 0.003,
    speed: 'fast',
    capabilities: ['Text', 'Vision', 'Code', 'Analysis'],
  },
  {
    id: 'claude-3-opus',
    name: 'Claude 3 Opus',
    provider: 'Anthropic',
    description: 'Most powerful Claude model for complex tasks',
    contextWindow: '200K',
    costPer1kTokens: 0.015,
    speed: 'slow',
    capabilities: ['Text', 'Vision', 'Code', 'Reasoning', 'Analysis'],
  },
  {
    id: 'gemini-1.5-pro',
    name: 'Gemini 1.5 Pro',
    provider: 'Google',
    description: 'Large context window for document analysis',
    contextWindow: '1M',
    costPer1kTokens: 0.00125,
    speed: 'medium',
    capabilities: ['Text', 'Vision', 'Code', 'Audio'],
  },
  {
    id: 'llama-3.1-70b',
    name: 'Llama 3.1 70B',
    provider: 'Meta',
    description: 'Open-source model with strong performance',
    contextWindow: '128K',
    costPer1kTokens: 0.0009,
    speed: 'fast',
    capabilities: ['Text', 'Code'],
  },
]

const SPEED_COLORS = {
  fast: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  slow: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
}

export function ModelStep({ state, updateState, errors }: ModelStepProps) {
  const primaryModel = MODELS.find((m) => m.id === state.primaryModel)
  const fallbackModel = MODELS.find((m) => m.id === state.fallbackModel)

  // Estimate monthly cost based on usage assumptions
  const estimateMonthlyCost = () => {
    if (!primaryModel) return 0
    // Assume 1000 requests/month, 2000 tokens average per request
    const tokensPerMonth = 1000 * 2000
    const primaryCost = (tokensPerMonth / 1000) * primaryModel.costPer1kTokens * 0.8 // 80% primary
    const fallbackCost = fallbackModel
      ? (tokensPerMonth / 1000) * fallbackModel.costPer1kTokens * 0.2 // 20% fallback
      : 0
    return primaryCost + fallbackCost
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Model Configuration
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          Select the AI models and configure their parameters.
        </p>
      </div>

      {/* Primary Model */}
      <div className="space-y-4">
        <Label className="text-gray-700 dark:text-gray-300 text-base font-semibold">
          Primary Model <span className="text-red-500">*</span>
        </Label>
        <Select
          value={state.primaryModel}
          onValueChange={(value) => updateState({ primaryModel: value })}
        >
          <SelectTrigger className={`rounded-xl ${errors.primaryModel ? 'border-red-500' : ''}`}>
            <SelectValue placeholder="Select a model" />
          </SelectTrigger>
          <SelectContent>
            {MODELS.map((model) => (
              <SelectItem key={model.id} value={model.id}>
                <div className="flex items-center gap-3">
                  <Cpu className="h-4 w-4 text-purple-500" />
                  <div>
                    <p className="font-medium">{model.name}</p>
                    <p className="text-xs text-gray-500">{model.provider} - {model.description}</p>
                  </div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.primaryModel && (
          <p className="text-sm text-red-500">{errors.primaryModel}</p>
        )}

        {/* Primary Model Details */}
        {primaryModel && (
          <div className="p-4 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white">
                  {primaryModel.name}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {primaryModel.provider}
                </p>
              </div>
              <Badge className={SPEED_COLORS[primaryModel.speed]}>
                <Zap className="h-3 w-3 mr-1" />
                {primaryModel.speed}
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500 dark:text-gray-400">Context</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {primaryModel.contextWindow}
                </p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Cost/1K tokens</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  ${primaryModel.costPer1kTokens}
                </p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Capabilities</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {primaryModel.capabilities.slice(0, 3).map((cap) => (
                    <Badge key={cap} variant="secondary" className="text-xs">
                      {cap}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Fallback Model */}
      <div className="space-y-4">
        <Label className="text-gray-700 dark:text-gray-300 text-base font-semibold">
          Fallback Model
        </Label>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Used when the primary model is unavailable or rate limited.
        </p>
        <Select
          value={state.fallbackModel}
          onValueChange={(value) => updateState({ fallbackModel: value })}
        >
          <SelectTrigger className="rounded-xl">
            <SelectValue placeholder="Select a fallback model" />
          </SelectTrigger>
          <SelectContent>
            {MODELS.filter((m) => m.id !== state.primaryModel).map((model) => (
              <SelectItem key={model.id} value={model.id}>
                <div className="flex items-center gap-3">
                  <Cpu className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="font-medium">{model.name}</p>
                    <p className="text-xs text-gray-500">{model.provider}</p>
                  </div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Temperature */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-gray-700 dark:text-gray-300 text-base font-semibold">
              Temperature
            </Label>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Controls randomness in responses
            </p>
          </div>
          <span className="text-lg font-semibold text-purple-600 dark:text-purple-400">
            {state.temperature.toFixed(1)}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500 w-20">Precise</span>
          <Slider
            value={[state.temperature]}
            onValueChange={([value]) => updateState({ temperature: value })}
            min={0}
            max={1}
            step={0.1}
            className="flex-1"
          />
          <span className="text-sm text-gray-500 w-20 text-right">Creative</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Info className="h-3.5 w-3.5" />
          Lower values = more consistent, higher values = more varied responses
        </div>
      </div>

      {/* Max Response Tokens */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-gray-700 dark:text-gray-300 text-base font-semibold">
              Max Response Length
            </Label>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Maximum tokens in each response
            </p>
          </div>
          <span className="text-lg font-semibold text-purple-600 dark:text-purple-400">
            {state.maxResponseTokens.toLocaleString()}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500 w-20">Short</span>
          <Slider
            value={[state.maxResponseTokens]}
            onValueChange={([value]) => updateState({ maxResponseTokens: value })}
            min={256}
            max={8192}
            step={256}
            className="flex-1"
          />
          <span className="text-sm text-gray-500 w-20 text-right">Long</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Info className="h-3.5 w-3.5" />
          ~{Math.round(state.maxResponseTokens * 0.75)} words maximum per response
        </div>
      </div>

      {/* Cost Estimate */}
      <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-800">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-xl bg-emerald-500">
            <DollarSign className="h-5 w-5 text-white" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white">
              Estimated Monthly Cost
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Based on ~1,000 requests/month
            </p>
          </div>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
            ${estimateMonthlyCost().toFixed(2)}
          </span>
          <span className="text-gray-500 dark:text-gray-400">/month</span>
        </div>
        <div className="mt-4 pt-4 border-t border-emerald-200 dark:border-emerald-700 grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500 dark:text-gray-400">Primary Model</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {primaryModel?.name || 'Not selected'}
            </p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">Fallback Model</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {fallbackModel?.name || 'None'}
            </p>
          </div>
        </div>
      </div>

      {/* Warning */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
        <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
            Cost varies based on actual usage
          </p>
          <p className="text-sm text-amber-700 dark:text-amber-400">
            Actual costs depend on request volume, input/output token counts, and model availability.
            Monitor usage in the Analytics dashboard.
          </p>
        </div>
      </div>
    </div>
  )
}
